import { useEffect, useRef, useState } from "react";
import { IDriver } from "../models/IDriver";
import { loadGoogleMaps } from "../utils/googleMapsLoader";

// Kurye panelden 15 sn'de bir konum yollar; bundan çok daha eski konum güvenilmez sayılır.
const STALE_AFTER_MS = 5 * 60 * 1000;

const STATUS_COLOR: Record<string, string> = {
  available: "#16a34a",
  ondelivery: "#ca8a04",
  offline: "#9ca3af",
};

const STATUS_LABEL: Record<string, string> = {
  available: "Müsait",
  ondelivery: "Teslimatta",
  offline: "Offline",
};

function timeAgo(iso?: string | null): string {
  if (!iso) return "bilinmiyor";
  const diffSec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return `${diffSec} sn önce`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min} dk önce`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function isStale(iso?: string | null): boolean {
  return !iso || Date.now() - new Date(iso).getTime() > STALE_AFTER_MS;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

interface Props {
  drivers: IDriver[];
  height?: string;
}

export default function DriversMap({ drivers, height = "380px" }: Props) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const infoRef = useRef<google.maps.InfoWindow | null>(null);
  const fittedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  const located = drivers.filter((d) => d.currentLatitude != null && d.currentLongitude != null);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapDivRef.current) return;
        mapRef.current = new google.maps.Map(mapDivRef.current, {
          center: { lat: 35.1856, lng: 33.3823 },
          zoom: 12,
          disableDefaultUI: true,
          zoomControl: true,
          fullscreenControl: true,
        });
        infoRef.current = new google.maps.InfoWindow();
        setReady(true);
      })
      .catch(() => { if (!cancelled) setError("Harita yüklenemedi."); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;

    const seen = new Set<string>();
    located.forEach((d) => {
      seen.add(d.id);
      const position = { lat: Number(d.currentLatitude), lng: Number(d.currentLongitude) };
      const statusKey = (d.status ?? "").toLowerCase();
      const stale = isStale(d.lastLocationUpdate);
      const color = stale ? "#9ca3af" : STATUS_COLOR[statusKey] ?? "#6366f1";
      const initials = (d.driverName ?? "?").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

      const icon: google.maps.Symbol = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 14,
        fillColor: color,
        fillOpacity: stale ? 0.55 : 1,
        strokeColor: "#fff",
        strokeWeight: 3,
      };
      const label: google.maps.MarkerLabel = { text: initials, color: "#fff", fontSize: "11px", fontWeight: "700" };

      let marker = markersRef.current.get(d.id);
      if (!marker) {
        marker = new google.maps.Marker({ map, position, icon, label, title: d.driverName });
        markersRef.current.set(d.id, marker);
      } else {
        marker.setPosition(position);
        marker.setIcon(icon);
        marker.setLabel(label);
      }

      const html = `
        <div style="font-family:inherit;min-width:170px">
          <div style="font-weight:700;font-size:.9rem;color:#111827">${escapeHtml(d.driverName ?? "Kurye")}</div>
          <div style="font-size:.78rem;color:${color};font-weight:700;margin-top:2px">${STATUS_LABEL[statusKey] ?? escapeHtml(d.status ?? "")}</div>
          ${d.driverPhone ? `<div style="font-size:.78rem;color:#374151;margin-top:4px">📞 ${escapeHtml(d.driverPhone)}</div>` : ""}
          ${d.vehiclePlate ? `<div style="font-size:.78rem;color:#374151">🚗 ${escapeHtml(d.vehiclePlate)}</div>` : ""}
          <div style="font-size:.72rem;color:${stale ? "#b91c1c" : "#6b7280"};margin-top:4px">
            Son konum: ${timeAgo(d.lastLocationUpdate)}${stale ? " (güncel değil)" : ""}
          </div>
        </div>`;
      google.maps.event.clearListeners(marker, "click");
      marker.addListener("click", () => {
        infoRef.current?.setContent(html);
        infoRef.current?.open({ map, anchor: marker });
      });
    });

    markersRef.current.forEach((marker, id) => {
      if (!seen.has(id)) {
        marker.setMap(null);
        markersRef.current.delete(id);
      }
    });

    // Sadece ilk konumlu yüklemede kadrajı ayarla; sonraki yenilemelerde kullanıcının
    // yakınlaştırdığı/kaydırdığı görünümü bozmamak için dokunma.
    if (!fittedRef.current && located.length > 0) {
      fittedRef.current = true;
      if (located.length === 1) {
        map.setCenter({ lat: Number(located[0].currentLatitude), lng: Number(located[0].currentLongitude) });
        map.setZoom(15);
      } else {
        const bounds = new google.maps.LatLngBounds();
        located.forEach((d) => bounds.extend({ lat: Number(d.currentLatitude), lng: Number(d.currentLongitude) }));
        map.fitBounds(bounds, 60);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, drivers]);

  const fitAll = () => {
    const map = mapRef.current;
    if (!map || located.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    located.forEach((d) => bounds.extend({ lat: Number(d.currentLatitude), lng: Number(d.currentLongitude) }));
    if (located.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(15);
    } else {
      map.fitBounds(bounds, 60);
    }
  };

  return (
    <div style={{ position: "relative", height, borderRadius: 14, overflow: "hidden", border: "1.5px solid #e5e7eb" }}>
      <div ref={mapDivRef} style={{ width: "100%", height: "100%", background: "#f1f5f9" }} />

      <div style={{
        position: "absolute", top: 10, left: 10, zIndex: 5,
        background: "#fff", borderRadius: 8, padding: "6px 10px",
        fontSize: ".72rem", fontWeight: 600, color: "#374151",
        boxShadow: "0 2px 10px rgba(0,0,0,0.15)", display: "flex", gap: 10, flexWrap: "wrap", maxWidth: "calc(100% - 20px)",
      }}>
        <span><i className="bi bi-circle-fill me-1" style={{ color: STATUS_COLOR.available }}></i>Müsait</span>
        <span><i className="bi bi-circle-fill me-1" style={{ color: STATUS_COLOR.ondelivery }}></i>Teslimatta</span>
        <span><i className="bi bi-circle-fill me-1" style={{ color: "#9ca3af" }}></i>Offline / 5 dk+ eski</span>
      </div>

      {located.length > 0 && (
        <button onClick={fitAll} title="Tüm kuryeleri göster"
          style={{
            position: "absolute", bottom: 12, left: 10, zIndex: 5,
            background: "#fff", border: "none", borderRadius: 8, padding: "6px 10px",
            fontSize: ".75rem", fontWeight: 700, color: "#374151", cursor: "pointer",
            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
          }}>
          <i className="bi bi-arrows-fullscreen me-1"></i>Tümünü Göster
        </button>
      )}

      {ready && located.length === 0 && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(255,255,255,0.7)", color: "#6b7280", fontSize: ".85rem", textAlign: "center", padding: 20,
        }}>
          <div>
            <i className="bi bi-geo-alt" style={{ fontSize: "1.8rem", display: "block", marginBottom: 6, opacity: 0.5 }}></i>
            Henüz konum paylaşan kurye yok.<br />
            Kuryeler panelde "çevrimiçi" olduğunda burada görünür.
          </div>
        </div>
      )}

      {!ready && !error && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      {error && (
        <div style={{
          position: "absolute", bottom: 10, left: 10, right: 10, zIndex: 5,
          background: "#fef2f2", border: "1.5px solid #fecaca", color: "#b91c1c",
          borderRadius: 8, padding: "8px 12px", fontSize: ".8rem",
        }}>
          <i className="bi bi-exclamation-circle me-1"></i>{error}
        </div>
      )}
    </div>
  );
}
